import { useState, useEffect, useCallback } from 'react';
import { apiClient, BASE_URL } from '../../core/api_client';
import {
  Announcement,
  ApartmentAddressOptionsResponse,
  ApartmentSaleSummaryResponse,
  SaleSummaryBuilding,
} from './types';
import {
  IProjectApartment,
  projectApartmentFromJson,
} from '../projects/project/data/entities';

/** Logging helpers — gated on a single flag so it's easy to silence
 *  later. Surface them in the browser DevTools console. */
const LOG = '[SaleAnnouncement]';

export function fetchAnnouncement(id: string): Promise<Announcement> {
  const url = `${BASE_URL}/api/front/announcements/${id}`;
  console.log(`${LOG} → GET`, url);
  return apiClient
    .get<Announcement>(`/announcements/${id}`)
    .then((r) => {
      console.log(`${LOG} ← 200`, url, r.data);
      return r.data;
    })
    .catch((err) => {
      console.error(`${LOG} ✕`, url, err?.response?.status, err?.response?.data ?? err.message);
      throw err;
    });
}

/** Resolve the announcement the page should show: the one flagged `is_new`.
 *
 *  Resolves to `null` when nothing is flagged — the caller redirects home
 *  rather than rendering an empty page. The list rows are summaries (no
 *  `content_*`, no `project_plans`), so the winner is re-fetched through
 *  `fetchAnnouncement` to get the full record the page renders from. */
export function fetchNewAnnouncement(fallbackToLatest = false): Promise<Announcement | null> {
  const url = `${BASE_URL}/api/front/announcements`;
  console.log(`${LOG} → GET`, url);
  return apiClient
    .get<Announcement[]>('/announcements')
    .then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      console.log(`${LOG} ← 200`, url, `${list.length} announcement(s)`);
      /* Matches the shared isNew() helper: the flag is strictly 1, not just
         truthy, so a stray "0" string never counts as new. */
      let flagged = list.filter((a) => Number(a.is_new) === 1);
      /* Preview mode: with nothing flagged, fall back to the latest one. */
      if (flagged.length === 0 && fallbackToLatest) flagged = list;
      if (flagged.length === 0) {
        console.warn(`${LOG} no announcement flagged is_new — nothing to show`);
        return null;
      }
      /* More than one flagged isn't expected, but pick the highest id so the
         choice is deterministic instead of depending on server ordering. */
      const picked = flagged.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a));
      console.log(
        `${LOG} is_new announcement id = ${picked.id}`,
        flagged.length > 1 ? `(${flagged.length} flagged, took highest id)` : '',
      );
      return fetchAnnouncement(String(picked.id));
    })
    .catch((err) => {
      console.error(
        `${LOG} ✕`,
        url,
        err?.response?.status,
        err?.response?.data ?? err.message,
      );
      throw err;
    });
}

interface UseAnnouncementResult {
  data: Announcement | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseNewAnnouncementResult extends UseAnnouncementResult {
  /** True once the list has loaded and nothing was flagged `is_new`.
   *  Distinct from `error` — the request succeeded, there is just no
   *  announcement to show, so the page redirects home. */
  noneFlagged: boolean;
}

/** Auto-firing variant of `fetchNewAnnouncement`, used by the page now that
 *  the route no longer carries an id. */
export function useNewAnnouncement(
  opts: { fallbackToLatest?: boolean; id?: string } = {},
): UseNewAnnouncementResult {
  const { fallbackToLatest = false, id } = opts;
  const [data, setData] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [noneFlagged, setNoneFlagged] = useState<boolean>(false);

  const load = useCallback(() => {
    console.log(`${LOG} useNewAnnouncement loading`);
    setLoading(true);
    setError(null);
    setNoneFlagged(false);
    (id ? fetchAnnouncement(id) : fetchNewAnnouncement(fallbackToLatest))
      .then((d) => {
        setData(d);
        setNoneFlagged(d === null);
        console.log(`${LOG} state.data set`, d);
      })
      .catch((e) => {
        setError(e);
        console.log(`${LOG} state.error set`, e);
      })
      .finally(() => setLoading(false));
  }, [fallbackToLatest, id]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, noneFlagged, refetch: load };
}

export function useAnnouncement(id: string | undefined): UseAnnouncementResult {
  const [data, setData] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(() => {
    if (!id) {
      console.warn(`${LOG} useAnnouncement called without an id — skipping fetch`);
      setLoading(false);
      setData(null);
      setError(new Error('useAnnouncement requires an announcement id.'));
      return;
    }
    console.log(`${LOG} useAnnouncement loading id=${id}`);
    setLoading(true);
    setError(null);
    fetchAnnouncement(id)
      .then((d) => {
        setData(d);
        console.log(`${LOG} state.data set`, d);
      })
      .catch((e) => {
        setError(e);
        console.log(`${LOG} state.error set`, e);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}

/** Query parameters supported by `GET /api/front/apartmentAddressOptions`.
 *  The endpoint is *cascading*: only the next-level array in the response
 *  is populated based on which params are supplied. */
export interface ApartmentAddressOptionsParams {
  projectPlanId: number;
  announcementId?: number;
  buildingNumber?: string | number;
  entranceNumber?: string | number;
  floorNumber?: string | number;
}

/** Fetch the next cascade level of address options. */
export function fetchApartmentAddressOptions(
  params: ApartmentAddressOptionsParams,
): Promise<ApartmentAddressOptionsResponse> {
  const qs = new URLSearchParams();
  qs.set('projectPlanId', String(params.projectPlanId));
  if (params.announcementId != null) {
    qs.set('announcementId', String(params.announcementId));
  }
  if (params.buildingNumber != null && params.buildingNumber !== '') {
    qs.set('buildingNumber', String(params.buildingNumber));
  }
  if (params.entranceNumber != null && params.entranceNumber !== '') {
    qs.set('entranceNumber', String(params.entranceNumber));
  }
  if (params.floorNumber != null && params.floorNumber !== '') {
    qs.set('floorNumber', String(params.floorNumber));
  }
  const url = `${BASE_URL}/api/front/apartmentAddressOptions?${qs.toString()}`;
  console.log(`${LOG} → GET`, url);
  return apiClient
    .get<ApartmentAddressOptionsResponse>(`/apartmentAddressOptions?${qs.toString()}`)
    .then((r) => {
      console.log(`${LOG} ← 200`, url, r.data);
      return r.data ?? {};
    })
    .catch((err) => {
      console.error(
        `${LOG} ✕`,
        url,
        err?.response?.status,
        err?.response?.data ?? err.message,
      );
      throw err;
    });
}

/* ---------------------------------------------------------------------
 * On-sale summary — announcement-scoped aggregates per building/floor
 * (room-type counts, entrance lists) used by MapSearchPanel.
 *
 * The sector payload only carries TOTAL inventory (`studio`, `oneRoom`,
 * `twoRoom`, `threeRoom`, `fourRoom`); this endpoint returns the
 * on-sale subset already grouped, so the map panel no longer needs the
 * full `/searchApartmentsV2` dump (~150 KB, ~7 s on Şirvan).
 * ------------------------------------------------------------------- */

/** Per-building / per-floor on-sale aggregates for a project under a
 *  given announcement. Single small GET; grouping happens server-side. */
export function fetchApartmentSaleSummary(
  projectPlanId: number,
  announcementId?: number,
): Promise<SaleSummaryBuilding[]> {
  const qs = new URLSearchParams();
  qs.set('projectPlanId', String(projectPlanId));
  if (announcementId != null) {
    qs.set('announcementId', String(announcementId));
  }
  const url = `${BASE_URL}/api/front/apartmentSaleSummary?${qs.toString()}`;
  console.log(`${LOG} → GET`, url);
  return apiClient
    .get<ApartmentSaleSummaryResponse>(`/apartmentSaleSummary?${qs.toString()}`)
    .then((r) => {
      const buildings = r.data?.buildings ?? [];
      console.log(`${LOG} ← 200`, url, `${buildings.length} building(s)`, r.data);
      return buildings;
    })
    .catch((err) => {
      console.error(
        `${LOG} ✕`,
        url,
        err?.response?.status,
        err?.response?.data ?? err.message,
      );
      throw err;
    });
}

/** All apartments selected for a project under a given announcement.
 *
 *  Single /searchApartmentsV2 call with no `limit` — the server
 *  returns the full set in one response and we consume it as-is.
 *  Still used by AddressSearchPanel as a row-enrichment lookup;
 *  MapSearchPanel has moved to `fetchApartmentSaleSummary`. */
export async function fetchOnSaleApartments(
  projectPlanId: number,
  announcementId?: number,
): Promise<IProjectApartment[]> {
  const body = {
    projectPlanId,
    announcementId,
    page: 1,
    data: {
      floor: { from: 1, to: 1000 },
      sectorTypes: [],
      repairTypes: [],
      roomCounts: [],
    },
  };
  console.log(
    `${LOG} → POST /searchApartmentsV2 (project ${projectPlanId})`,
    body,
  );
  try {
    const r = await apiClient.post('/searchApartmentsV2', body);
    const raw = (r.data?.data ?? []) as any[];
    const parsed = raw.map(projectApartmentFromJson);
    console.log(
      `${LOG} ← /searchApartmentsV2 (project ${projectPlanId}) — ${parsed.length} rows (total ${r.data?.total})`,
      { response: r.data, parsed },
    );
    return parsed;
  } catch (err: any) {
    console.error(
      `${LOG} ✕ /searchApartmentsV2 (project ${projectPlanId})`,
      err?.response?.status,
      err?.response?.data ?? err.message,
    );
    return [];
  }
}

interface UseApartmentAddressOptionsResult {
  data: ApartmentAddressOptionsResponse;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Auto-firing variant of `fetchApartmentAddressOptions`. Re-fetches
 *  whenever any input changes; returns the latest response. */
export function useApartmentAddressOptions(
  params: Partial<ApartmentAddressOptionsParams> & { projectPlanId?: number },
): UseApartmentAddressOptionsResult {
  const {
    projectPlanId,
    announcementId,
    buildingNumber,
    entranceNumber,
    floorNumber,
  } = params;
  const [data, setData] = useState<ApartmentAddressOptionsResponse>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(() => {
    if (!projectPlanId) {
      setData({});
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchApartmentAddressOptions({
      projectPlanId,
      announcementId,
      buildingNumber,
      entranceNumber,
      floorNumber,
    })
      .then((d) => setData(d))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [projectPlanId, announcementId, buildingNumber, entranceNumber, floorNumber]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}
